import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState, // Added useState
} from 'react';
import ReactDOM from 'react-dom';
import useInitializeMap from './hooks/useInitializeMap';
import useMapManager from './hooks/useMapManager';
import { useLocation } from 'react-router';
import LocationNavigation from './LocationNavigation';
import LocationInfoBox from './LocationInfoBox';
import type { Content, Location } from '@plone/types';
import useDynamicLocations from './hooks/useDynamicLocations';
import { useIntl } from 'react-intl';
import { LocationMapMessages as messages } from './LocationMapMessages';

export type MapState = {
  map: mapboxgl.Map | null;
  activeLocation: Location;
  isSetFitBounds: boolean;
  isOneOfThesePages: boolean;
  isClient: boolean;
  isFullscreen: boolean;
};

export type MapAction =
  | {
      type: 'SET_MAP_AND_IS_CLIENT';
      payload: { map: mapboxgl.Map; isClient: boolean };
    }
  | { type: 'SET_FIT_BOUNDS'; payload: boolean }
  | {
      type: 'SET_LOCATION_AND_BOUNDS';
      payload: { location: Location; fitBounds: boolean };
    }
  | { type: 'SET_ONE_OF_THESE_PAGES'; payload: boolean }
  | { type: 'SET_FULLSCREEN'; payload: boolean };

function mapReducer(state: MapState, action: MapAction): MapState {
  switch (action.type) {
    case 'SET_MAP_AND_IS_CLIENT':
      return {
        ...state,
        map: action.payload.map,
        isClient: action.payload.isClient,
      };
    case 'SET_FIT_BOUNDS':
      return { ...state, isSetFitBounds: action.payload };
    case 'SET_LOCATION_AND_BOUNDS':
      return {
        ...state,
        activeLocation: action.payload.location,
        isSetFitBounds: action.payload.fitBounds,
      };
    case 'SET_ONE_OF_THESE_PAGES':
      return { ...state, isOneOfThesePages: action.payload };
    case 'SET_FULLSCREEN':
      return { ...state, isFullscreen: action.payload };
    default:
      return state;
  }
}

interface LocationMapProps {
  content: Content;
  oneOfThesePages: string[];
  locationsData: Location[];
}

// Constants
const MARKER_OFFSET_Y = -35;
const DEFAULT_MAP_HEIGHT = '670px';
const MOBILE_MAP_HEIGHT = '360px';
const MOBILE_BREAKPOINT = '(max-width: 768px)';

const LocationMapContent: React.FC<LocationMapProps> = ({
  content,
  oneOfThesePages,
  locationsData,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const intl = useIntl();
  const [mapHeight, setMapHeight] = useState<string>(DEFAULT_MAP_HEIGHT);
  const [markerOffsetX, setMarkerOffsetX] = useState<number>(-29);

  // Calculate responsive marker offset based on screen width
  const calculateMarkerOffset = useCallback(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width === 1024) {
        return 90;
      } else if (width === 1280) {
        return -29;
      } else if (width > 1024 && width < 1280) {
        // Linear interpolation between 1024px (90) and 1280px (-29)
        const ratio = (width - 1024) / (1280 - 1024);
        return Math.round(90 + ratio * (-29 - 90));
      }
    }
    return -29; // Default value for all other screen sizes (mobile, desktop > 1280px, etc.)
  }, []);

  // Create dynamic marker offset constants (maintaining backward compatibility)
  const MARKER_OFFSET_X = markerOffsetX;
  const MARKER_OFFSET: [number, number] = [MARKER_OFFSET_X, MARKER_OFFSET_Y];

  const { locations } = useDynamicLocations({
    content,
    location,
    oneOfThesePages,
    locationsData,
  });

  const initialState = useMemo(
    () => ({
      map: null as mapboxgl.Map | null,
      activeLocation: locations[0],
      isSetFitBounds: false,
      isOneOfThesePages: true,
      isClient: false,
      isFullscreen: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locations[0]?.['@id']], // Only depend on the first location's ID, not the entire array
  );

  const [state, dispatch] = useReducer(mapReducer, initialState);

  useInitializeMap({
    mapContainerRef,
    state,
    dispatch,
    location,
    oneOfThesePages,
  });

  const { handleLocationClick } = useMapManager({
    locationsData: locations,
    state,
    dispatch,
    location,
    oneOfThesePages,
    offset: MARKER_OFFSET, // Use dynamic responsive offset
    mapInstance: state.map as mapboxgl.Map,
  });

  const renderLocationInfoBox = () => (
    <LocationInfoBox location={state.activeLocation} />
  );

  const renderInfoBoxInFullscreen = () =>
    state.isFullscreen &&
    !state.isSetFitBounds &&
    ReactDOM.createPortal(
      renderLocationInfoBox(),
      state.map?.getContainer() || document.body,
    );

  useEffect(() => {
    const href = '/static/css/mapbox-gl.css';

    const link = document.createElement('link');
    link.href = href;
    link.rel = 'preload';
    link.as = 'style';
    link.onload = () => {
      link.rel = 'stylesheet';
    };

    document.head.appendChild(link);

    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, []);

  useEffect(() => {
    const calculateMapHeight = () => {
      if (typeof window !== 'undefined' && window.matchMedia) {
        try {
          return window.matchMedia(MOBILE_BREAKPOINT).matches
            ? MOBILE_MAP_HEIGHT
            : DEFAULT_MAP_HEIGHT;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error using matchMedia:', error);
          return DEFAULT_MAP_HEIGHT;
        }
      }
      return DEFAULT_MAP_HEIGHT;
    };

    const handleResize = () => {
      setMapHeight(calculateMapHeight());
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle responsive marker offset
  useEffect(() => {
    const handleMarkerOffsetResize = () => {
      setMarkerOffsetX(calculateMarkerOffset());
    };

    handleMarkerOffsetResize(); // Set initial value
    window.addEventListener('resize', handleMarkerOffsetResize);

    return () => {
      window.removeEventListener('resize', handleMarkerOffsetResize);
    };
  }, [calculateMarkerOffset]);

  return (
    locations &&
    locations.length > 0 && (
      <div className="location-map">
        {state.isOneOfThesePages && state.isClient && (
          <>
            <h1 className="text-size-lg text-weight-semibold centered">
              {intl.formatMessage(messages.oneOfThesePagesTitle)}
            </h1>
            <p className="centered pb-lg">
              {intl.formatMessage(messages.oneOfThesePagesDescription)}
            </p>
          </>
        )}
        {content?.locations_title && (
          <h2 className="text-size-lg text-weight-semibold has-b-border locations-title">
            {content.locations_title}
          </h2>
        )}
        {content?.locations_description && (
          <p className="locations-description">
            {content.locations_description}
          </p>
        )}
        {!state.isOneOfThesePages && (
          <LocationNavigation
            locations={locations}
            activeLocation={state.activeLocation}
            setActiveLocation={handleLocationClick}
          />
        )}
        <div className="location-map-container">
          {renderInfoBoxInFullscreen()}
          {!state.isFullscreen &&
            !state.isSetFitBounds &&
            renderLocationInfoBox()}
          <div
            key={location.pathname}
            ref={mapContainerRef}
            style={{ width: '100%', height: mapHeight }}
          />
        </div>
      </div>
    )
  );
};

export default LocationMapContent;
